import {createSelector} from '@reduxjs/toolkit'

import {eq, isNull, minZero} from '@shared/helpers/lang'
import {roundUpToTenThousand} from '@shared/helpers/math'

import {PayMethod, PreferredPoint, PAYMONEY_RESULT_TYPE} from '@web/core/constants'
import {getPayMoneyInfo} from '@web/helpers/policies'
import {accountSelector} from '@web/slices/account/slice'
import {chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {orderEntrySelector, pageSetInfoSelector} from '@web/slices/common/slice'
import {expenseDetailsSelector, orderAmountSelector} from '@web/slices/expenseDetails/slice'
import {naverPayPointSelector, pointPaymentSelector} from '@web/slices/pointPayment/slice'
import {
    availableAddingRechargePointSelector,
    naverBankbookOpenedSelector,
    selectedMethodSelector,
    hasWithdrawalAccountSelector,
} from '@web/slices/priorityPaymentMethod/slice'
import {rewardPointPolicy, rewardPointSelector} from '@web/slices/rewardPoint/slice'

import {getAccumulateType} from './utils'

export const pointAmountWithLimitPolicySelector = createSelector(
    chargePointSelector,
    rewardPointSelector,
    payMoneyInfoSelector,
    pageSetInfoSelector,
    (chargePoint, rewardPoint, {isPayMoneyUser}, isPaymentMethodManagement) => {
        const {maximumHoldingPoint} = chargePoint

        const chargePointAmount = isPaymentMethodManagement
            ? chargePoint.amount
            : Math.min(maximumHoldingPoint, chargePoint.amount)
        const rewardPointAmount = isPaymentMethodManagement
            ? rewardPoint.amount
            : Math.min(maximumHoldingPoint, rewardPoint.amount)

        return {
            chargePointAmount,
            rewardPointAmount,
            totalPointAmount: isPayMoneyUser ? rewardPointAmount : chargePointAmount + minZero(rewardPointAmount),
        }
    },
)

/**
 * toggle type의 pointPayment component selector
 */
export const pointTogglePaymentSelector = createSelector(
    pointPaymentSelector,
    pointAmountWithLimitPolicySelector,
    orderAmountSelector,
    payMoneyInfoSelector,
    ({naverPayPoint: {usePointAll}}, {chargePointAmount, rewardPointAmount}, orderAmount, {isPayMoneyUser}) => {
        const usingChargePoint = Math.max(chargePointAmount, 0)
        const usingRewardPoint = Math.max(rewardPointAmount, 0)
        const usingNaverPayPoint = isPayMoneyUser ? usingRewardPoint : usingChargePoint + usingRewardPoint

        const usingAmount = usePointAll ? Math.min(orderAmount, usingNaverPayPoint) : 0
        const remainAmount = Math.max(orderAmount - usingAmount, 0)

        return {
            /** 실제 사용 예상 포인트  */
            usingAmount,
            /** 포인트 사용 후 남은 결제금액 */
            remainAmount,
            /** 예상 충전 금액 (1만원 단위) */
            expectedRechargePointAmount: roundUpToTenThousand(
                isPayMoneyUser ? Math.max(remainAmount - chargePointAmount, 0) : remainAmount,
                0,
            ),
        }
    },
)

/** 1순위 결제수단 기준으로 이번 결제에서 사용되는 금액정보 */
export const usingAmountSelector = createSelector(
    payMoneyInfoSelector,
    chargePointSelector,
    rewardPointSelector,
    pointPaymentSelector,
    orderAmountSelector,
    naverPayPointSelector,
    selectedMethodSelector,
    (
        {isPayMoneyUser},
        {amount: chargePointAmount},
        {amount: rewardPointAmount},
        {naverPayPoint: {usePointAll}},
        orderAmount,
        {preferredInfo: {type}},
        selectedMethod,
    ) => {
        const chargePointFirst = eq(type, PreferredPoint.CHARGE_POINT) && !isPayMoneyUser

        const usingRewardPointAmount = usePointAll
            ? Math.min(rewardPointAmount, minZero(orderAmount - (chargePointFirst ? chargePointAmount : 0)))
            : 0
        const usingChargePointAmount =
            usePointAll && !isPayMoneyUser
                ? Math.min(chargePointAmount, minZero(orderAmount - (chargePointFirst ? 0 : rewardPointAmount)))
                : 0
        const priceToBePaid = orderAmount - usingRewardPointAmount - usingChargePointAmount
        const usingRechargePointAmount = eq(selectedMethod, PayMethod.RECHARGE_POINT) ? priceToBePaid : 0
        const usingPgAmount = !eq(selectedMethod, PayMethod.NONE) ? priceToBePaid - usingRechargePointAmount : 0

        return {
            /** 사용되는 적립 포인트 */
            usingRewardPointAmount,
            /** 사용되는 보유 페이머니 */
            usingChargePointAmount,
            /** 사용되는 충전결제 금액 */
            usingRechargePointAmount,
            /** 실제 PG사에서 결제할 금액 */
            usingPgAmount,
            /** 총 사용 금액 */
            usingTotalAmount:
                usingRewardPointAmount + usingChargePointAmount + usingRechargePointAmount + usingPgAmount,
        }
    },
)

/** 아이템 별 사용 금액 계산 (1순위 결제수단 기준) */
export const usingAmountByItemSelector = createSelector(
    expenseDetailsSelector,
    usingAmountSelector,
    orderAmountSelector,
    payMoneyInfoSelector,
    (
        {billing, items},
        {usingRewardPointAmount, usingChargePointAmount, usingRechargePointAmount},
        totalPrice,
        {availablePayment},
    ) => {
        return rewardPointPolicy.getUsingPointAmountByItem({
            items: items.map((item) => ({
                cpId: billing?.cpId,
                itemId: billing?.itemId,
                price: item.price - item.discount,
            })),
            totalPrice,
            usingRewardPointAmount,
            usingTotalChargePointAmount: availablePayment ? usingChargePointAmount + usingRechargePointAmount : 0,
        })
    },
)

/** 충전결제시 상태 */
export const chargeStatusSelector = createSelector(
    naverBankbookOpenedSelector,
    availableAddingRechargePointSelector,
    accountSelector,
    usingAmountSelector,
    payMoneyInfoSelector,
    (
        /** 충전결제 - 네이버 통장 선택 여부 */
        isSelectedNaverBankbook,
        /** 선택한 계좌로 충전이 가능한지 여부 */
        canUseRechargeAccount,
        /** 현재 보유 페이머니가 이전에 네이버통장으로 충전되었는지 여부 */
        {naverBankbookBenefitOnRecentPayment},
        /** 사용되는 보유 페이머니, 사용되는 충전결제 금액 */
        {usingChargePointAmount, usingRechargePointAmount},
        /** 페이머니 유저 여부 */
        {availablePayment, payMoneyUserStatus},
    ) => {
        const payMoneyInfo = getPayMoneyInfo(payMoneyUserStatus)

        const useRechargePointWithoutNaverAccount = !isSelectedNaverBankbook && usingRechargePointAmount > 0
        const isAvailableUser = !payMoneyInfo.isConnected && availablePayment
        const rechargingWithNaverAccount =
            isAvailableUser && isSelectedNaverBankbook && canUseRechargeAccount && usingRechargePointAmount > 0
        const usingPointChargedWithNaverAccount =
            isAvailableUser &&
            naverBankbookBenefitOnRecentPayment &&
            usingChargePointAmount > 0 &&
            // 네통이 아닌 다른 계좌로 충전되는 케이스는 제외
            !useRechargePointWithoutNaverAccount

        return {
            /** 네이버통장으로 충전하는지 여부 */
            rechargingWithNaverAccount,
            /** 사용되는 페이머니가 네이버통장으로 충전됐는지 여부 */
            usingPointChargedWithNaverAccount,
        }
    },
)

/** 충전포인트 충전 제한 여부 */
export const rechargePointReloadingLimitedSelector = createSelector(
    chargePointSelector,
    orderAmountSelector,
    payMoneyInfoSelector,
    pointTogglePaymentSelector,
    (
        {reloadable: {limit: limitedAmount, apiResult: chargeLimitResultType}, actualAmount},
        orderAmount,
        {isPayMoneyUser},
        {usingAmount},
    ) => {
        const remainAmount = orderAmount - usingAmount
        const expectedRechargeAmount = roundUpToTenThousand(remainAmount, 0)

        /**
         * 충전한도 조회 실패시에는
         * 충전금액, 월 충전한도 남은 금액, 미성년자 월 충전한도 남은금액이
         * 0으로 처리, but 충전결제 선택할 수 있는 상태이므로 false 반환
         */
        if (chargeLimitResultType !== PAYMONEY_RESULT_TYPE.SUCCESS) {
            return false
        }

        /** 페이머니 회원의 경우, 보유 페이머니 잔액이 충전예상 잔액보다 크다면 선택가능 */
        if (isPayMoneyUser && actualAmount > expectedRechargeAmount) {
            return false
        }

        /** 월 충전한도 계산 시 포인트 사용여부 제외한 결제금액을 충전 예상금액으로 측정 */
        return !isNull(limitedAmount) && roundUpToTenThousand(remainAmount, 0) > limitedAmount
    },
)

/** 머니 보유한도 초과로 인한 포인트 전액사용 DIALOG 여부  */
export const overBalanceUseAllPointSelector = createSelector(
    payMoneyInfoSelector,
    pointTogglePaymentSelector,
    chargePointSelector,
    pointPaymentSelector,
    (
        {payMoneyUserResultType, chargeLimitResultType, isPayMoneyUser},
        {expectedRechargePointAmount},
        {reloadable: {amount: reloadableRechargePointAmount}},
        {naverPayPoint: {usePointAll}},
    ) => {
        const resultTypeEqualsToSuccess = eq(PAYMONEY_RESULT_TYPE.SUCCESS)
        return (
            resultTypeEqualsToSuccess(payMoneyUserResultType) &&
            resultTypeEqualsToSuccess(chargeLimitResultType) &&
            !isPayMoneyUser &&
            expectedRechargePointAmount > reloadableRechargePointAmount &&
            !usePointAll
        )
    },
)

/**
 * - 적립률은 API의 응답값 expectedAccumulateRate 사용
 */
export const rechargePointAccumulateSelector = createSelector(
    hasWithdrawalAccountSelector,
    naverBankbookOpenedSelector,
    payMoneyInfoSelector,
    orderEntrySelector,
    chargePointSelector,
    (
        hasWithdrawalAccount,
        naverBankbookSelected,
        {isPayMoneyUser},
        {isBooking},
        {recharging: {expectedAccumulateRate}},
    ) => {
        const accumulateType = getAccumulateType({
            isBooking,
            hasWithdrawalAccount,
            isPayMoneyUser,
            naverBankbookSelected,
        })

        return {
            accumulateType,
            accumulateRate: expectedAccumulateRate ?? 0,
        }
    },
)
